import { useState, useEffect } from 'react';
import useStore from '../store/useStore';
import { FiCheck, FiX, FiRefreshCw, FiZap, FiHelpCircle } from 'react-icons/fi';
import './MIDIConnection.css';
import { useToast } from './Toast';

/**
 * MIDI Connection Component
 * Handles USB MIDI and LoopMIDI port selection
 */

function MIDIConnection() {
    // Selector subscriptions: avoid re-rendering on unrelated store changes.
    const midiInitialized = useStore(state => state.midiInitialized);
    const midiInputs = useStore(state => state.midiInputs);
    const midiOutputs = useStore(state => state.midiOutputs);
    const selectedInputId = useStore(state => state.selectedInputId);
    const selectedOutputId = useStore(state => state.selectedOutputId);
    const isConnected = useStore(state => state.isConnected);
    const connectionStatus = useStore(state => state.connectionStatus);
    const deviceType = useStore(state => state.deviceType);
    const initMIDI = useStore(state => state.initMIDI);
    const refreshPorts = useStore(state => state.refreshPorts);
    const connectMIDI = useStore(state => state.connectMIDI);
    const disconnectMIDI = useStore(state => state.disconnectMIDI);
    const autoConnect = useStore(state => state.autoConnect);
    const setDeviceType = useStore(state => state.setDeviceType);
    const notify = useToast();

    const [selectedInput, setSelectedInput] = useState(selectedInputId);
    const [selectedOutput, setSelectedOutput] = useState(selectedOutputId);
    const [showHelp, setShowHelp] = useState(false);

    const handleConnect = () => {
        if (selectedInput && selectedOutput) {
            const result = connectMIDI(selectedInput, selectedOutput);
            if (!result.success) {
                notify(`❌ Connection failed: ${result.error}`, 'error');
            }
        }
    };

    const handleDisconnect = () => {
        disconnectMIDI();
        setSelectedInput(null);
        setSelectedOutput(null);
    };

    const handleRefresh = () => {
        refreshPorts();
    };

    const handleAutoConnect = async () => {
        const result = await autoConnect();
        if (result.success) {
            notify(`✅ Connected to ${result.portName || 'Looper'} via USB!`, 'success');
        } else {
            const detail = result.error ? `\n\n(${result.error})` : '';
            notify(`No Looper device found.\n\nPlease ensure:\n1. USB cable is connected\n2. Device is powered on\n3. "USB-MIDI" is enabled in Looper settings${detail}`, 'error');
        }
    };

    // Initialize on mount if not already initialized
    useEffect(() => {
        if (!midiInitialized) {
            initMIDI();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="midi-connection glass">
            <div className="midi-connection-header">
                <div className="header-title-group">
                    <h3>🎹 MIDI Connection</h3>
                    <button
                        className={`help-btn ${showHelp ? 'active' : ''}`}
                        onClick={() => setShowHelp(!showHelp)}
                        title="Connection Help"
                    >
                        <FiHelpCircle />
                    </button>
                    {showHelp && (
                        <div className="help-popout">
                            <h4>🔌 USB Connection Issues?</h4>
                            <p>If your device is plugged in but not showing up:</p>
                            <ul>
                                <li><strong>USB Mode Conflict:</strong> If you are in "USB Transfer Mode" (file transfer), MIDI is <strong>disabled</strong> on the device.</li>
                                <li><strong>Fix:</strong> Eject the drive from PC and exit the USB screen on your Looper to return to normal mode.</li>
                                <li><strong>Cable:</strong> Ensure you are using a data-capable USB cable.</li>
                            </ul>
                            <button className="close-help" onClick={() => setShowHelp(false)}>Close</button>
                        </div>
                    )}
                </div>
                <div className="device-selector-row">
                    <div className="device-selector">
                        <label>Device:</label>
                        <select
                            value={deviceType}
                            onChange={(e) => setDeviceType(e.target.value)}
                            className="device-select"
                        >
                            <option value="looper-plus">Looper Plus (2+2 footswitches)</option>
                            <option value="looper-x">Looper X (8 footswitches)</option>
                        </select>
                    </div>
                    <div className={`connection-status status-${isConnected ? 'connected' : 'disconnected'}`}>
                        {isConnected ? (
                            <>
                                <FiCheck /> Connected
                            </>
                        ) : (
                            <>
                                <FiX /> Disconnected
                            </>
                        )}
                    </div>
                </div>
            </div>

            {connectionStatus === 'error' && (
                <div className="error-message">
                    <strong>⚠️ Web MIDI API not available</strong>
                    <p>Please use Chrome, Edge, or Opera browser with HTTPS enabled.</p>
                </div>
            )}

            {midiInitialized && (
                <>
                    <div className="midi-selectors">
                        <div className="selector-group">
                            <label htmlFor="midi-input">MIDI Input (from Looper)</label>
                            <select
                                id="midi-input"
                                value={selectedInput || ''}
                                onChange={(e) => setSelectedInput(e.target.value)}
                                disabled={isConnected}
                                className={isConnected && selectedInput ? 'input-connected' : ''}
                            >
                                <option value="">Select input...</option>
                                <optgroup label="USB Devices">
                                    {midiInputs.filter(i => i.name.toLowerCase().includes('usb') || i.name.toLowerCase().includes('looper')).map(input => (
                                        <option key={input.id} value={input.id}>
                                            {input.name} {selectedInput === input.id && isConnected ? '(Connected)' : ''}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Other Devices">
                                    {midiInputs.filter(i => !i.name.toLowerCase().includes('usb') && !i.name.toLowerCase().includes('looper')).map(input => (
                                        <option key={input.id} value={input.id}>
                                            {input.name} {selectedInput === input.id && isConnected ? '(Connected)' : ''}
                                        </option>
                                    ))}
                                </optgroup>
                            </select>
                        </div>

                        <div className="selector-group">
                            <label htmlFor="midi-output">MIDI Output (to Looper)</label>
                            <select
                                id="midi-output"
                                value={selectedOutput || ''}
                                onChange={(e) => setSelectedOutput(e.target.value)}
                                disabled={isConnected}
                                className={isConnected && selectedOutput ? 'input-connected' : ''}
                            >
                                <option value="">Select output...</option>
                                <optgroup label="USB Devices">
                                    {midiOutputs.filter(o => o.name.toLowerCase().includes('usb') || o.name.toLowerCase().includes('looper')).map(output => (
                                        <option key={output.id} value={output.id}>
                                            {output.name} {selectedOutput === output.id && isConnected ? '(Connected)' : ''}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Other Devices">
                                    {midiOutputs.filter(o => !o.name.toLowerCase().includes('usb') && !o.name.toLowerCase().includes('looper')).map(output => (
                                        <option key={output.id} value={output.id}>
                                            {output.name} {selectedOutput === output.id && isConnected ? '(Connected)' : ''}
                                        </option>
                                    ))}
                                </optgroup>
                            </select>
                        </div>
                    </div>

                    <div className="midi-actions">
                        <button
                            className="btn-refresh"
                            onClick={handleRefresh}
                            disabled={isConnected}
                            title="Refresh MIDI ports"
                        >
                            <FiRefreshCw /> Refresh
                        </button>

                        {isConnected && (
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    console.log('🎵 Testing MIDI Output...');
                                    // Send Note 11 (Record/Play) which is the default for FS1
                                    const success = useStore.getState().sendNoteOn(11, 127, 1);
                                    if (success) {
                                        // Must be a real Note Off — a second Note On with
                                        // velocity 0 leaves the note stuck on hardware.
                                        setTimeout(() => useStore.getState().sendNoteOff(11, 1), 100);
                                        // Optional: alert('Sent Rec/Play Command!');
                                    } else {
                                        notify('❌ Failed to send note.\nCheck console for details.\n(Is a MIDI Output device connected?)', 'error');
                                        console.error('Test Note Failed. Store state:', useStore.getState());
                                    }
                                }}
                                title="Send Test Command (Rec/Play)"
                            >
                                🎵 Test (Rec/Play)
                            </button>
                        )}

                        {!isConnected ? (
                            <>
                                <button
                                    className="btn-secondary"
                                    onClick={handleAutoConnect}
                                    title="Auto-detect Looper via USB"
                                >
                                    <FiZap /> Auto Connect
                                </button>
                                <button
                                    className="btn-primary"
                                    onClick={handleConnect}
                                    disabled={!selectedInput || !selectedOutput}
                                >
                                    <FiCheck /> Connect
                                </button>
                            </>
                        ) : (
                            <button
                                className="btn-danger"
                                onClick={handleDisconnect}
                            >
                                <FiX /> Disconnect
                            </button>
                        )}
                    </div>

                    {midiInputs.length === 0 && midiOutputs.length === 0 && (
                        <div className="info-message">
                            <p><strong>💡 No MIDI ports detected</strong></p>
                            <p>Connect your Looper via USB cable, or ensure LoopMIDI is running.</p>
                            <p className="troubleshooting-tip">
                                ⚠️ <strong>Note:</strong> If you are in "USB Transfer Mode" (loading files), MIDI will
                                <strong> NOT</strong> work. Please disconnect storage mode on the device to control it.
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default MIDIConnection;
